import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { inventoryApi } from "./inventoryApi";
import { formatDateTime } from "./inventoryConstants";

export const CycleCountSection = ({ currentUser, canManage }) => {
  const [parts, setParts] = useState([]);
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partId, setPartId] = useState("");
  const [actual, setActual] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([inventoryApi.getParts(), inventoryApi.getCycleCounts()]);
      setParts(p); setCounts(c);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const selectedPart = parts.find((p) => p.id === partId);
  const system = selectedPart?.quantity_on_hand ?? null;
  const variance = (actual !== "" && system !== null) ? Number(actual) - system : null;

  const submit = async () => {
    if (!partId) { toast.error("Select a part"); return; }
    if (actual === "" || isNaN(Number(actual))) { toast.error("Enter actual counted quantity"); return; }
    setSaving(true);
    try {
      const res = await inventoryApi.createCycleCount({ part_id: partId, actual_quantity: Number(actual), counted_by: userName, notes });
      toast.success(res.adjustment_made ? `Variance ${res.variance > 0 ? "+" : ""}${res.variance} adjusted` : "Count matched — no variance");
      setActual(""); setNotes("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="cycle-count-section">
      {canManage && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-blue-500" /> New Cycle Count</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Part</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={partId} onChange={(e) => setPartId(e.target.value)} data-testid="count-part-select">
                <option value="">Select part...</option>
                {parts.map((p) => <option key={p.id} value={p.id}>{p.part_number} — {p.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs text-slate-500">System Quantity</Label><Input value={system ?? ""} disabled className="bg-slate-50" /></div>
            <div><Label className="text-xs text-slate-500">Actual Counted</Label><Input type="number" value={actual} onChange={(e) => setActual(e.target.value)} data-testid="count-actual-input" /></div>
          </div>
          {variance !== null && variance !== 0 && (
            <div className={`text-sm font-medium ${variance > 0 ? "text-green-600" : "text-red-600"}`} data-testid="count-variance">
              Variance: {variance > 0 ? "+" : ""}{variance} — an inventory adjustment will be created automatically
            </div>
          )}
          {variance === 0 && <div className="text-sm text-slate-500">No variance — counts match</div>}
          <div><Label className="text-xs text-slate-500">Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <Button onClick={submit} disabled={saving} data-testid="count-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Count"}</Button>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Count History</h3>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : counts.length === 0 ? (
          <div className="text-center py-8 text-slate-400">No cycle counts recorded</div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
            {counts.map((c) => {
              const match = c.variance === 0;
              const up = c.variance > 0;
              const chip = match ? "bg-slate-100" : up ? "bg-green-50" : "bg-red-50";
              const ic = match ? "text-slate-500" : up ? "text-green-600" : "text-red-600";
              return (
                <div key={c.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors" data-testid="count-row">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${chip}`}><ClipboardCheck className={`w-4 h-4 ${ic}`} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.part_name} {c.part_number && <span className="text-[10px] font-mono text-slate-400">{c.part_number}</span>}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">{formatDateTime(c.created_at)} · {c.counted_by} · {c.system_quantity} → {c.actual_quantity}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-sm font-semibold tabular-nums ${match ? "text-slate-400" : up ? "text-green-600" : "text-red-600"}`}>{up ? "+" : ""}{c.variance}</div>
                    {c.adjustment_made && <div className="text-[10px] text-amber-600">adjusted</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
